import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { RELAY_SOLANA_MAINNET_CHAIN_ID } from "../../../../anyspend/constants/index.js";
import { ALL_CHAINS } from "../../../../anyspend/utils/chain.js";
import { toast, useAccountWallet } from "../../../../global-account/react/index.js";
import { TokenSelector } from "@relayprotocol/relay-kit-ui";
import { ChevronDown, Copy } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useMemo, useState } from "react";
import { base } from "thirdweb/chains";
import ModalHeader from "../ModalHeader/ModalHeader.js";
import { Button } from "../ui/button.js";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu.js";
// Coinbase logo SVG
const CoinbaseLogo = () => _jsxs("svg", {
  width: "16",
  height: "16",
  viewBox: "0 0 16 16",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  children: [_jsx("path", {
    d: "M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16Z",
    fill: "#0052FF"
  }), _jsx("path", {
    d: "M8.00065 11.3337C6.16065 11.3337 4.66732 9.84033 4.66732 8.00033C4.66732 6.16033 6.16065 4.66699 8.00065 4.66699C9.47399 4.66699 10.734 5.64699 11.1607 7.00033H13.2273C12.774 4.54699 10.6273 2.66699 8.00065 2.66699C5.05399 2.66699 2.66732 5.05366 2.66732 8.00033C2.66732 10.947 5.05399 13.3337 8.00065 13.3337C10.6273 13.3337 12.774 11.4537 13.2273 9.00033H11.1607C10.734 10.3537 9.47399 11.3337 8.00065 11.3337Z",
    fill: "white"
  })]
});
export function Deposit() {
  const {
    address
  } = useAccountWallet();
  const [selectedChainId, setSelectedChainId] = useState(base.id);
  const [selectedToken, setSelectedToken] = useState({
    address: "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48",
    chainId: base.id,
    decimals: 6,
    symbol: "USDC",
    name: "USD Coin",
    metadata: {
      logoURI: "https://assets.relay.link/icons/1/light/usdc.png"
    }
  });
  // Get the selected chain object
  const selectedChain = useMemo(() => ALL_CHAINS[selectedChainId], [selectedChainId]);
  // Get all supported chains for the dropdown
  const supportedChains = useMemo(() => Object.values(ALL_CHAINS), []);
  // The deposit address is the user's active wallet address
  const depositAddress = address;
  const handleTokenSelect = token => {
    setSelectedChainId(token.chainId);
    setSelectedToken({
      address: token.address,
      chainId: token.chainId,
      decimals: token.decimals,
      metadata: {
        logoURI: token.logoURI
      },
      name: token.name,
      symbol: token.symbol
    });
  };
  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(depositAddress || "");
      toast.success("Address copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy address");
    }
  };
  return _jsxs("div", {
    className: "flex w-full flex-col",
    children: [_jsx(ModalHeader, {
      title: "Deposit",
      showCloseButton: false
    }), _jsxs("div", {
      className: "flex flex-col pb-5 pt-5",
      children: [_jsx("div", {
        className: "mb-4 px-5",
        children: _jsx("p", {
          className: "font-neue-montreal-medium text-base leading-snug text-[#3f3f46]",
          children: "Send any accepted token and we'll convert it to B3 on Base for you to use."
        })
      }), _jsxs("div", {
        className: "space-y-3 px-5",
        children: [_jsxs("div", {
          children: [_jsx("label", {
            className: "mb-1.5 block text-sm font-medium text-[#3f3f46]",
            children: "Chain"
          }), _jsxs(DropdownMenu, {
            children: [_jsx(DropdownMenuTrigger, {
              asChild: true,
              children: _jsxs("button", {
                className: "flex h-10 w-full items-center justify-between rounded-lg border border-[#d1d1d6] bg-white px-2.5 py-2 transition-colors hover:bg-[#fafafa]",
                children: [_jsxs("div", {
                  className: "flex items-center gap-2",
                  children: [selectedChain?.logoUrl && _jsx("img", {
                    src: selectedChain.logoUrl,
                    alt: selectedChain.name,
                    className: "h-6 w-6 rounded-full"
                  }), _jsx("span", {
                    className: "text-base text-[#18181b]",
                    children: selectedChain?.name || "Select Chain"
                  })]
                }), _jsx(ChevronDown, {
                  className: "h-4 w-4 text-[#51525c]"
                })]
              })
            }), _jsx(DropdownMenuContent, {
              className: "max-h-[300px] w-full overflow-y-auto",
              children: supportedChains.map(chain => _jsx(DropdownMenuItem, {
                onClick: () => setSelectedChainId(chain.id),
                children: _jsxs("div", {
                  className: "flex items-center gap-2",
                  children: [chain.logoUrl && _jsx("img", {
                    src: chain.logoUrl,
                    alt: chain.name,
                    className: "h-6 w-6 rounded-full"
                  }), _jsx("span", {
                    className: "text-[#18181b]",
                    children: chain.name
                  })]
                })
              }, chain.id))
            })]
          })]
        }), _jsxs("div", {
          children: [_jsx("label", {
            className: "mb-1.5 block text-sm font-medium text-[#3f3f46]",
            children: "Accepted tokens"
          }), _jsx(TokenSelector, {
            address: address,
            chainIdsFilter: [selectedChainId],
            context: "from",
            fromChainWalletVMSupported: true,
            isValidAddress: true,
            lockedChainIds: [selectedChainId],
            multiWalletSupportEnabled: true,
            onAnalyticEvent: undefined,
            popularChainIds: [1, 8453, RELAY_SOLANA_MAINNET_CHAIN_ID],
            setToken: handleTokenSelect,
            supportedWalletVMs: ["evm", "svm"],
            token: undefined,
            trigger: _jsxs("button", {
              className: "flex h-10 w-full items-center justify-between rounded-lg border border-[#d1d1d6] bg-white px-2.5 py-2 transition-colors hover:bg-[#fafafa]",
              children: [_jsxs("div", {
                className: "flex items-center gap-2",
                children: [selectedToken?.metadata?.logoURI && _jsx("img", {
                  src: selectedToken.metadata.logoURI,
                  alt: selectedToken.symbol,
                  className: "h-6 w-6 rounded-full"
                }), _jsx("span", {
                  className: "text-base text-[#18181b]",
                  children: selectedToken.symbol
                })]
              }), _jsx(ChevronDown, {
                className: "h-4 w-4 text-[#51525c]"
              })]
            })
          })]
        })]
      }), _jsxs("div", {
        className: "mx-5 mt-4 flex items-start rounded-xl border border-[#e4e4e7] bg-[#fafafa]",
        children: [_jsxs("div", {
          className: "flex flex-1 flex-col items-center gap-2 px-5 pb-3 pt-5",
          children: [_jsx("div", {
            className: "flex w-full items-center justify-center",
            children: _jsx("div", {
              className: "w-full",
              children: _jsx("div", {
                className: "flex aspect-[144/146] w-full flex-col items-center justify-center overflow-hidden",
                children: _jsx(QRCodeSVG, {
                  value: depositAddress || "",
                  size: 144,
                  level: "M"
                })
              })
            })
          }), _jsxs("div", {
            className: "flex items-center gap-2",
            children: [_jsx("span", {
              className: "text-xs font-semibold leading-[18px] text-[#0b57c2]",
              children: "SCAN WITH"
            }), _jsx(CoinbaseLogo, {})]
          })]
        }), _jsxs("div", {
          className: "relative flex flex-1 flex-col gap-2 self-stretch border-l border-[#e4e4e7] p-6",
          children: [_jsx("label", {
            className: "text-sm font-medium leading-5 text-[#3f3f46]",
            children: "Deposit address:"
          }), _jsxs("div", {
            className: "relative flex w-full flex-wrap items-center gap-2",
            children: [_jsx("p", {
              className: "h-[74px] flex-1 overflow-hidden text-ellipsis whitespace-pre-wrap break-all pr-6 text-base font-semibold leading-6 text-[#18181b]",
              children: depositAddress
            }), _jsx("button", {
              onClick: handleCopyAddress,
              className: "absolute bottom-0 right-0 h-4 w-4 transition-opacity hover:opacity-70",
              "aria-label": "Copy address",
              children: _jsx(Copy, {
                className: "h-full w-full text-[#51525c]"
              })
            })]
          })]
        })]
      }), _jsx("div", {
        className: "mx-5 mt-4",
        children: _jsxs("p", {
          className: "font-neue-montreal-italic text-center text-sm leading-[1.3] text-[#3f3f46]",
          children: ["Do not send any tokens other than the ones specified.", _jsx("br", {}), "Tokens not accepted will not be converted."]
        })
      }), _jsx("div", {
        className: "mt-4 px-5",
        children: _jsx(Button, {
          onClick: handleCopyAddress,
          className: "h-12 w-full rounded-xl bg-[#0c68e9] text-base font-semibold text-white shadow-[inset_0px_0px_0px_1px_rgba(10,13,18,0.18),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)] hover:bg-[#0b5fd4]",
          children: "Copy deposit address"
        })
      })]
    })]
  });
}